import { Shape, CanvasContextLike } from './types';

/**
 * Helper to draw an array of shapes to a specific context.
 * Moved from @hcie/canvas-ui to @hcie/shared to break circular dependencies.
 */
export function drawShapesToCtx(targetCtx: CanvasContextLike, shapes: Shape[]) {
    if (!shapes || shapes.length === 0) return;

    const ctx = targetCtx as CanvasRenderingContext2D;
    ctx.save();
    shapes.forEach(shape => {
        ctx.save();

        // Setup style
        const lineWidth = shape.style.width || 1;
        const strokeColor = shape.style.color || '#000';
        ctx.lineWidth = lineWidth;
        ctx.strokeStyle = strokeColor;
        ctx.globalAlpha = shape.style.opacity !== undefined ? shape.style.opacity : 1.0;
        ctx.lineCap = shape.style.cap || 'round';

        // Hardness: 1 = sharp edge, 0 = soft (blurred) edge; applied via shadowBlur for vector strokes
        const hardness = shape.style.hardness !== undefined ? shape.style.hardness : 1.0;
        if (hardness < 1.0) {
            const maxBlur = Math.max(2, lineWidth / 2);
            ctx.shadowBlur = (1.0 - hardness) * maxBlur;
            ctx.shadowColor = strokeColor;
        }

        if (shape.style.fillColor) {
            ctx.fillStyle = shape.style.fillColor;
        }

        if (shape.style.angle && shape.type !== 'line') {
            let cx = (shape.x1 + shape.x2) / 2;
            let cy = (shape.y1 + shape.y2) / 2;
            if (shape.type === 'circle') {
                cx = shape.x1;
                cy = shape.y1;
            } else {
                cx = (Math.min(shape.x1, shape.x2) + Math.max(shape.x1, shape.x2)) / 2;
                cy = (Math.min(shape.y1, shape.y2) + Math.max(shape.y1, shape.y2)) / 2;
            }
            ctx.translate(cx, cy);
            ctx.rotate(shape.style.angle * Math.PI / 180);
            ctx.translate(-cx, -cy);
        }

        ctx.beginPath();
        switch (shape.type) {
            case 'line':
                ctx.moveTo(shape.x1, shape.y1);
                ctx.lineTo(shape.x2, shape.y2);
                ctx.stroke();
                break;
            case 'rect': {
                const w = shape.x2 - shape.x1;
                const h = shape.y2 - shape.y1;
                ctx.rect(shape.x1, shape.y1, w, h);
                if (shape.style.fill) ctx.fill();
                ctx.stroke();
                break;
            }
            case 'circle': {
                const radius = Math.hypot(shape.x2 - shape.x1, shape.y2 - shape.y1);
                ctx.arc(shape.x1, shape.y1, radius, 0, 2 * Math.PI);
                if (shape.style.fill) ctx.fill();
                ctx.stroke();
                break;
            }
            case 'ellipse': {
                const centerX = (shape.x1 + shape.x2) / 2;
                const centerY = (shape.y1 + shape.y2) / 2;
                const radiusX = Math.abs(shape.x2 - shape.x1) / 2;
                const radiusY = Math.abs(shape.y2 - shape.y1) / 2;
                ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2);
                if (shape.style.fill) ctx.fill();
                ctx.stroke();
                break;
            }
            case 'roundrect': {
                const rw = shape.x2 - shape.x1;
                const rh = shape.y2 - shape.y1;
                const rr = shape.style.cornerRadius || 10;
                if (typeof (ctx as any).roundRect === 'function') {
                    (ctx as any).roundRect(shape.x1, shape.y1, rw, rh, rr);
                } else {
                    const x = shape.x1, y = shape.y1, w = rw, h = rh, r = rr;
                    ctx.moveTo(x + r, y);
                    ctx.lineTo(x + w - r, y);
                    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
                    ctx.lineTo(x + w, y + h - r);
                    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
                    ctx.lineTo(x + r, y + h);
                    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
                    ctx.lineTo(x, y + r);
                    ctx.quadraticCurveTo(x, y, x + r, y);
                }
                if (shape.style.fill) ctx.fill();
                ctx.stroke();
                break;
            }
        }

        ctx.restore();
    });
    ctx.restore();
}

/**
 * Robust color parser using a hidden canvas.
 * @param colorStr CSS color string (hex, rgb, name)
 * @param opacity 0.0 to 1.0
 * @returns {r, g, b, a} 0-255
 */
export function parseColorToRgba(colorStr: string, opacity: number = 1.0) {
    if (typeof document === 'undefined') return { r: 0, g: 0, b: 0, a: 255 };
    const tempCan = document.createElement('canvas');
    tempCan.width = tempCan.height = 1;
    const tCtx = tempCan.getContext('2d');
    if (!tCtx) return { r: 0, g: 0, b: 0, a: 255 };
    tCtx.fillStyle = colorStr;
    tCtx.fillRect(0, 0, 1, 1);
    const data = tCtx.getImageData(0, 0, 1, 1).data;
    return {
        r: data[0],
        g: data[1],
        b: data[2],
        a: Math.floor(opacity * 255)
    };
}
